import { Component, inject, OnInit, ChangeDetectionStrategy, input, ChangeDetectorRef } from '@angular/core';

import { EntPrimaryTableConfig } from '@ng/ent-components/ent-tables';
import { EntLoaderService } from '@ng/ent-components/ent-loader';

import { DownloadFileService } from 'src/app/shared/services/download-file.service';
import { UtilsService } from 'src/app/shared/services/utils.service';
import { OutstandingCommentsTableConfig } from '../outstanding-requirement-form/outstanding-requirement-comments-table-config';
import { Message, OutstandingCommentsTableRowsPerPage } from 'src/config/constants';
import { BRERequirement, File } from 'src/app/shared/models/app.model';
import { environment } from 'src/environments/environment.prod';
import { OutstandingRequirementService } from 'src/app/shared/services/outstanding-requirement.service';
import { AgentService } from 'src/app/shared/services/agent.service';
import moment from 'moment';
import { FileMetadataService } from 'src/app/shared/services/file-metadata.service';
import { AnalyticsAppService } from 'src/app/shared/services/analytics.service';
import { NgStyle, NgClass } from '@angular/common';
import { EntModalPopupModule } from '@ng/ent-components/ent-modal-popup';
import { EntFormWidgetsModule } from '@ng/ent-components/ent-form-widgets';
import { EntButtonTrayModule } from '@ng/ent-components/ent-button-tray';


@Component({
    selector: 'app-download-file',
    templateUrl: './download-file.component.html',
    styleUrls: ['./download-file.component.scss'],
    imports: [EntModalPopupModule, EntFormWidgetsModule, EntButtonTrayModule, NgStyle, NgClass],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DownloadFileComponent implements OnInit {
  private readonly utilsService = inject(UtilsService);
  private readonly downloadFileService = inject(DownloadFileService);
  private readonly entLoaderService = inject(EntLoaderService);
  private readonly agentService = inject(AgentService);
  private readonly outstandingReqService = inject(OutstandingRequirementService);
  private readonly fileMetadataService = inject(FileMetadataService);
  private readonly analytics = inject(AnalyticsAppService);
  private readonly cdr = inject(ChangeDetectorRef);


  /** Position of this requirement in the parent list. */
  readonly index = input(0);

  /** Requirement whose downloadable files are displayed and updated. */
  readonly BRERequirement = input.required<BRERequirement>();

  /** Comments table configuration and data updated after a download. */
  readonly outstandingCommentsTableCustomData = input.required<EntPrimaryTableConfig>();

  /** Indicates whether the current user has home-office permissions. */
  readonly isHOUser = input<boolean>(false);

  outstandingCommentsTableConfig: EntPrimaryTableConfig = OutstandingCommentsTableConfig;
  outstandingCommentsTableRowsPerPage: number = OutstandingCommentsTableRowsPerPage;
  downloadFileModalConfig = {
    header: true,
    state: true,
    footer: true
  }
  isDownloadSuccess!: boolean;
  isDownloadFailure!: boolean;
  environment: any = environment;
  message = Message;
  isFileSelected = false;
  requirementFileList: any;
  allFileList: any;
  emptyFileListError = false;
  fileMetadata: any;
  requirementFileMetadata: any;

  ngOnInit() {
    this.fileMetadata = this.fileMetadataService.getFileMetadata()
    this.requirementFileMetadata = this.fileMetadata?.requirements?.find((file:any) => {
      return file.requirementId === this.BRERequirement().ID;
    })?.files;
    if (!this.requirementFileMetadata) {
      this.emptyFileListError = true;
      this.cdr.markForCheck();
      return;
    }
    this.utilsService.setFocus('modalPopupClose');
  }

  /**
   * close download file Modal
   * @returns {void}
   */
  closedownloadFileModal(): void {
    this.downloadFileModalConfig.state = false;
    this.BRERequirement().DownloadFile = {
      ModalState: false,
    }
    this.utilsService.setFocus(`downloadFileBtn-${this.BRERequirement().ID}`);
  }

  /**
   * download selected file api call, on success show success msg for 5 sec and then hide it
   * @returns {void}
   */
  downloadFile(file: File): void {
    this.entLoaderService.show();
    this.resetProps();
    const agentProfile = this.agentService.getAgentProfile();
    const userName = `${agentProfile?.lastName?.trim() || ''}, ${agentProfile?.firstName?.trim() || ''}`;
    const commentData = {
      date:  new Date(),
      userName: userName,
      comment: `(${file.fileName}) was downloaded successfully.`
    }
    this.downloadFileService.downloadFile(file.fileName, this.BRERequirement()?.ID).subscribe((res: any) => {
      if (res.responseStatus == "SUCCESS") {
        const fileData = 'data:application/octet-stream;base64,' + res.data[0];
        const downloadLink = document.createElement('a');
        const fileName = file.fileName;
        downloadLink.href = fileData;
        downloadLink.download = fileName;
        downloadLink.click();
        this.entLoaderService.hide();
        file.downloaded = true;
        this.isDownloadSuccess = true;
        this.cdr.markForCheck();
        this.callAnnuityAPI(this.BRERequirement()?.ID || '', commentData);
        setTimeout(() => {
          this.isDownloadSuccess = false;
          this.cdr.markForCheck();
        }, 5000)
        return;
      }
      if (res == '500') {
        this.entLoaderService.hide();
        this.isDownloadFailure = true;
        this.cdr.markForCheck();
      }
    })
    this.analytics.logAnalytics({
      event_action: 'button',
      event_name: 'download file',
      event_type: 'pending tool - annuity',
      event_version: 'requirement'
    });
  }

  /**
   * call ANBA api to add comment for the downloaded file
   * @param id - BRERequirement ID
   * @param commentData - date, username, comment text
   */
  callAnnuityAPI(id: string, commentData: any): void {
    this.outstandingReqService.requirementResolution('Agent', commentData, id || '')
      .subscribe((resp: any) => {
        if (resp === 'success') {
          this.BRERequirement().TableData?.unshift({
            dateandtime: moment(commentData.date).format('MM/DD/YYYY hh:mm A'),
            user: commentData.userName,
            comment: commentData.comment
          })
          this.outstandingCommentsTableCustomData().config.rowsPerPage = this.outstandingCommentsTableRowsPerPage;
          this.setAgentComments(commentData);
          this.cdr.markForCheck();
          return;
        }
        if (resp == '500') {
          return;
        }
    });
  }

  /**
   * Add file download details to Agent comments object
   * @param commentData - ID, comment text, date, agent name
   */
  setAgentComments(commentData: any): any {
      this.utilsService.getAgentComments().push({
        ID: this.BRERequirement().ID,
        AgentCommentText: commentData.comment,
        AgentCommentDate: moment(commentData.date).format('MM/DD/YYYY hh:mm A'),
        AgentName: commentData.userName
      })
  }

  /**
   * reset success, error message properties
   * @returns {void}
   */
  resetProps(): void {
    this.isDownloadSuccess = false;
    this.isDownloadFailure = false;
  }

  onTab(e: KeyboardEvent, ind?: number): void {
    if (ind != this.requirementFileMetadata?.length - 1) {
      return;
    }
    if (!e.shiftKey && e.code === 'Tab') {
      this.utilsService.setFocus('modalPopupClose');
      e.preventDefault();
    }
  }

}
